import { useFormik } from "formik";
import { ReactNode, useEffect, useState } from "react";
import {
  useAddPayoutAccountMutation,
  useUpdatePayoutAccountMutation,
} from "../../redux/features/payment/payment.api";
import {
  PayoutAccountPayload,
  PayoutCurrency,
  SinglePayoutAccount,
} from "../../redux/features/payment/payment.types";
import { useAppSelector } from "../../redux/hooks";
import { currencyOptions } from "../../utils/constants";
import {
  AddPayoutSchema,
  CADPayoutSchema,
  NGNPayoutSchema,
} from "../../utils/validations";
import ComboBox from "../dropdown/comboBox";
import { CaretDown } from "../icon/downcaret";
import { CADPayoutForm, ConfirmCADPayoutForm } from "./cadPayoutForm";
import { NGNPayoutForm } from "./ngnPayoutForm";

export const PayoutForm = ({
  children,
  className = "",
  onSuccess = () => {},
  mode = "create",
  type,
  account,
  hideCurrencySelector = false,
  onStepChange = () => {},
  isModalDisplay,
}: {
  children: (props: {
    handleSubmit: () => void;
    loading: boolean;
    disabled: boolean;
  }) => ReactNode;
  className?: string;
  onSuccess?: (props: { resetForm: () => void }) => void;
  mode?: "create" | "edit";
  type?: PayoutCurrency;
  account?: SinglePayoutAccount;
  hideCurrencySelector?: boolean;
  onStepChange?: (step: number) => void;
  isModalDisplay?: boolean;
}) => {
  const [disableProceed, setDisableProceed] = useState<boolean>(false);
  const [schema, setSchema] = useState(AddPayoutSchema);
  const [step, setStep] = useState<number>(1);

  const { hasPayoutAccount } = useAppSelector((state) => state.auth);

  const [addPayout, { isLoading: addingPayout }] =
    useAddPayoutAccountMutation();

  const [updatePayout, { isLoading: updatingPayout }] =
    useUpdatePayoutAccountMutation();

  const formik = useFormik({
    initialValues: {
      accountName: account?.accountName,
      accountNumber: account?.accountNumber,
      bankName: account?.bankName,
      bankCode: account?.bankCode,
      swiftRoutingNumber: account?.swiftRoutingNumber,
      interacName: account?.interacName,
      interacEmail: account?.interacEmail,
      currency: type,
      confirm: account?.accountNumber,
    } as PayoutAccountPayload & { confirm: string },
    enableReinitialize: true,
    validationSchema: schema,
    onSubmit: async (vals) => {
      const { confirm, ...payload } = vals;

      if (step === 1 && vals.currency === "Canadian Dollars") {
        setStep(2);
        return;
      } else {
        if (mode === "create") {
          const resp = await addPayout({
            ...payload,
          });

          if (resp.data?.status === "success") {
            onSuccess({
              resetForm: () => {
                formik.resetForm();
              },
            });
          }
        } else {
          const resp = await updatePayout({
            ...payload,
          });

          if (resp.data?.status === "success") {
            onSuccess({
              resetForm: () => {
                formik.resetForm();
              },
            });
          }
        }
      }
    },
  });

  useEffect(() => {
    switch (formik.values.currency) {
      case "NGN":
        setSchema(NGNPayoutSchema);
        break;
      case "Canadian Dollars":
        setSchema(CADPayoutSchema);
        break;
      //   case "US Dollars":
      //     setSchema(USDPayoutSchema);
      //     break;
      default:
        setSchema(AddPayoutSchema);
        break;
    }
    // eslint-disable-next-line
  }, [formik.values.currency]);

  useEffect(() => {
    onStepChange(step);
    // eslint-disable-next-line
  }, [step]);

  useEffect(() => {
    return () => {
      formik.resetForm();
      setStep(1);
    };
    // eslint-disable-next-line
  }, []);

  return (
    <form
      onSubmit={formik.handleSubmit}
      className={`grid grid-cols-1 gap-4 ${className}`}
    >
      {step === 1 ? (
        <>
          {mode === "edit" || hideCurrencySelector ? undefined : (
            <ComboBox
              menuItems={currencyOptions.filter(
                (o) => !hasPayoutAccount[o.value],
              )}
              onSelectOption={(selected) => {
                formik.setFieldValue("currency", selected);
              }}
              menuClassName="!max-h-[200px] translate-x-[-12px] !z-[999]"
              position="bottom start"
              immediate
              type="text"
              name="currency"
              value={formik.values.currency}
              placeholder={"Select currency"}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              hasTitle
              title={"Currency"}
              errors={
                formik.errors.currency && formik.touched.currency
                  ? formik.errors.currency
                  : ""
              }
              readOnly
              containerClassName="col-span-full"
              customIcon={<CaretDown />}
              customIconPosition="right"
            />
          )}

          {formik.values.currency === "NGN" ? (
            <NGNPayoutForm
              formik={formik}
              setDisable={(val) => {
                setDisableProceed(val);
              }}
            />
          ) : undefined}

          {formik.values.currency === "Canadian Dollars" ? (
            <CADPayoutForm formik={formik} />
          ) : undefined}

          {/* {formik.values.currency === "US Dollars" ? (
        <USDPayoutForm formik={formik} />
      ) : undefined} */}
        </>
      ) : undefined}

      {step === 2 ? (
        <>
          {formik.values.currency === "Canadian Dollars" ? (
            <ConfirmCADPayoutForm
              formik={formik}
              isModalDisplay={isModalDisplay}
            />
          ) : undefined}
        </>
      ) : undefined}

      {children({
        handleSubmit: formik.handleSubmit,
        loading: addingPayout || updatingPayout,
        disabled: addingPayout || updatingPayout || disableProceed,
      })}
    </form>
  );
};
